/**
 * AccountLayout — the account area's shell: masthead, index, routed outlet.
 * STORY-001-010 Implementation logic §4.
 *
 * THE 2026-08 REBUILD
 *
 * Before: a motif-textured rounded box holding an h1 and three pill-shaped
 * nav links, floating beside a column of floating cards. The nav read as three
 * loose buttons rather than as an index of one document, and the motif — a
 * decorative texture behind the ONLY orienting element on the screen — reduced
 * the contrast of the thing the user needed most.
 *
 * After: a slip masthead (display-face title + one line saying what the area is
 * for) over a drawn INDEX. Desktop keeps the index as a sticky left column
 * ruled off from the content by a real vertical rule; mobile turns it into a
 * ruled horizontal strip where the active entry is marked by a drawn underline
 * rather than by a coloured pill. Both are the same object: a ledger index,
 * with the current page stated, not merely highlighted.
 *
 * The active entry is NEVER signalled by colour alone (WCAG 1.4.1): it carries
 * `aria-current="page"` from NavLink, a heavier weight, and a drawn marker.
 *
 * Every `data-testid` (`account-layout`, `account-nav-*`, `account-outlet`) is
 * preserved — `axe-account-screens.spec.ts` and the account e2e suite key off
 * them.
 */
import clsx from "clsx";
import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Fade, Stagger, StaggerItem } from "@/components/motion";
import { registerAccountI18n } from "@/features/account/api";

const NAV_ITEMS = [
  { to: "/account/profile", key: "nav.profile", testId: "account-nav-profile" },
  { to: "/account/sessions", key: "nav.sessions", testId: "account-nav-sessions" },
  { to: "/account/privacy", key: "nav.privacy", testId: "account-nav-privacy" },
] as const;

export function AccountLayout() {
  // Register the `account` i18n namespace at render — matching the other
  // account screens. The bare side-effect import alone is not enough: the
  // module-load eager registration bails when i18n isn't yet initialized (a
  // race against initI18n), and AccountLayout renders its nav BEFORE the child
  // screen's registerAccountI18n() runs — so the index showed raw keys
  // ("nav.profile") while the screen itself was translated.
  registerAccountI18n();
  const { t } = useTranslation("account");

  return (
    <div className="min-h-full bg-[var(--g-ground)]">
      <main
        className="mx-auto w-full max-w-[980px] px-4 pb-28 pt-6 md:px-6 md:pb-16 md:pt-10"
        data-testid="account-layout"
      >
        <Fade className="mb-5 md:mb-7">
          <h1 className="font-display text-[27px] font-extrabold leading-[1.05] text-foreground md:text-[34px]">
            {t("title")}
          </h1>
          <p className="mt-2 max-w-[46ch] text-[14px] leading-[1.5] text-[var(--g-ink-2)]">
            {t("lede")}
          </p>
        </Fade>

        <div className="md:grid md:grid-cols-[13rem_1fr] md:gap-8">
          {/* The index. A real <nav> keeps the landmark + accessible name; the
              Stagger orchestrates the entries (it forwards className/testid but
              not aria-label, so the label lives on the <nav>). This layout
              persists across sub-routes — only the Outlet swaps — so the
              entrance runs once and never re-staggers on tab change. */}
          <nav
            aria-label={t("title")}
            className="mb-6 md:mb-0 md:sticky md:top-6 md:self-start"
          >
            {/* WRAPS on mobile rather than scrolling: with three entries whose
                Kyrgyz labels run 15–25% longer than the Russian, a horizontal
                scroller cuts the third entry mid-word at the viewport edge and
                gives no hint that it scrolls. Two short rows beat one clipped
                one. */}
            <Stagger className="flex flex-wrap gap-x-5 border-b border-[var(--g-rule-strong)] md:flex-col md:gap-x-0 md:border-b-0 md:border-r">
              {NAV_ITEMS.map((item) => (
                // The motion lives on the StaggerItem wrapper; the NavLink keeps
                // its className function, data-testid, and (NavLink's own)
                // aria-current="page", so the account-nav-* selectors and the
                // screen-reader current-tab announcement survive the rebuild.
                <StaggerItem key={item.to} className="md:w-full">
                  <NavLink
                    to={item.to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      clsx(
                        "relative flex min-h-[44px] items-center py-2.5 text-[14px] transition-colors md:py-3 md:pr-4",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                        // The active marker is DRAWN — an underline on mobile,
                        // a right-hand rule on desktop — so the state survives
                        // greyscale and does not rely on the tint.
                        isActive
                          ? [
                              "font-bold text-foreground",
                              "after:absolute after:bg-[var(--g-primary)] after:content-['']",
                              "after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px]",
                              "md:after:bottom-0 md:after:left-auto md:after:right-[-1px] md:after:top-0 md:after:h-auto md:after:w-[2px]",
                            ]
                          : "font-medium text-[var(--g-ink-2)] hover:text-foreground",
                      )
                    }
                  >
                    {t(item.key)}
                  </NavLink>
                </StaggerItem>
              ))}
            </Stagger>
          </nav>

          {/* Outlet stays a plain <section>: each child screen owns its own
              entrance. Wrapping it here would re-fire on every tab switch (the
              index persists, only the outlet swaps) and fight the child. */}
          <section className="min-w-0" data-testid="account-outlet">
            <Outlet />
          </section>
        </div>
      </main>
    </div>
  );
}

export default AccountLayout;
